import { action, computed, observable, runInAction, makeObservable} from 'mobx';
import Http from '../Http';
import authStore from './authStore';
import tilikausiStore from './tilikausiStore';
import routing from './routingStore';
import viewStore from './viewStore';
import Storage from '../Storage';
import utils, { shallowDiffers, monthNoToName, round2d, parseError } from '../utils';

const slicestring = '/tasevastaavaa';
const slicelength = 14;
const pushname = 'Tase vastaavaa';
const baseurl = 'tasevastaavaa/';
const boundurl = 'tasevastaavaa';

const CACHE_TIME = 1800000; // 1000 * 60 * 30
const SHOW_MORE_LIMIT = 5;


class tasevastaavaaStore {
  @observable accounts = new Map();
  @observable loadingAccounts = new Map();
  @observable selectedMonth = null;

  @observable profits = new Map();

  @observable categoryLimits = new Map();

  @observable loadingCategories = new Map();
  @observable loadingProfits = new Map();
  @observable loadingProfitsBackground = new Map();

  forceUpdatingAccounts = {};

  pageContentRef;

  // If no data is received from the use [noData] to show notification to the user.
  @observable noData = false;

  persistCategoryLimits = false;
  scrollTop = 0;

  // last sync times
  profitsSynced = {};
  accountsSynced = {};
  avaavataseArray = [];
  avaavatase = 0;
  paattyvatase = 0;

  constructor() {
    makeObservable(this);
    const profits = Storage.get('profits');

    if (profits) {
      this.profits.replace(profits);
    }

    const accounts = Storage.get('accounts');

    if (accounts) {
      this.accounts.replace(accounts);
    }
  }

  kirjauskausiToMonthName = (kirjauskausi) => {
    if (!this.profit) return;

    const total = kirjauskausi + this.profit.startmonth - 1;
    const month = total % 12;
    const year = this.profit.startyear + Math.floor((total - 1) / 12);

    return monthNoToName(month) + ' ' + year;
  }


  @computed
  get currentTilikausiId() {
    return tilikausiStore.currentTilikausi ? tilikausiStore.currentTilikausi.TILIKAUSIID : authStore.currentCompany.tilikausi;
  }

  @computed
  get hasModule() {
    return authStore.hasModule(12);
  }

  @computed
  get timespan() {
    if (!this.profit || !this.profit.startdate) return '';

    return utils.ymd2dmy(this.profit.startdate) + ' - ' + utils.ymd2dmy(this.profit.enddate);
  }

  @computed
  get isLoadingAccounts() {
    return this.loadingAccounts.has(this.accountToken);
  }

  @computed
  get accountToken() {
    return [this.profitToken, this.filteredBranch.TILINRO, this.selectedMonth || 'all'].join('.');
  }

  @computed
  get account() {
    return this.accounts.get(this.accountToken);
  }

  @computed
  get isLoadingProfits() {
    return this.loadingProfits.has(this.profitToken);
  }

  @computed
  get isLoadingProfitsBackground() {
    return this.loadingProfitsBackground.has(this.profitToken);
  }

  @computed
  get profitToken() {
    return authStore.companyToken + '.' + this.currentTilikausiId;
  }

  @computed
  get profit() {
    return this.profits.get(this.profitToken);
  }


  @computed
  get avaavatasemonthText() {
    if (!this.selectedMonth) {
      return '';
    }

    return this.kirjauskausiToMonthName(this.selectedMonth);
  }

  @computed
  get paattyvatasemonthText() {
    if (!this.selectedMonth) {
      return 'Tilikauden tase';
    }

    return 'Tase ' + this.kirjauskausiToMonthName(this.selectedMonth);
  }

  @computed
  get invalidHash() {
    if (this.computered === 0) {
      return true;
    }

    return false;
  }

  @computed
  get total() {

    if (this.filteredBranch) {
      return this.sum(this.filteredBranch);
    }
    return null;
  }

  @computed.struct
  get computered() {
    if (!this.profit || routing.location.pathname.slice(0, slicelength) !== slicestring) {
      return false;
    }

    let path = slicestring;
    let branch = this.profit;
    let level;
    let crumbs = [];
    let siblings;
    let treePath = routing.location.pathname.slice(slicelength + 1);
    let treeParams = treePath && treePath.split('/');

    crumbs.push({
      name: pushname,
      path
    });

    if (treeParams) {
      for (let m of treeParams) {
        if (!branch || !branch.children) {
          return 0;
        }
        if(branch.children.length > m){
          level = branch.children[m];
        }
        else {
          level = null;
        }

        if (level && level.TILINRO) {
          return 0;
        }

        //TODO: refactor everything from indexes to id's
        //Kun !level niin ei voi muodostaa joukkoa tilinumeron eikä muunkaan perusteella
        //Pitäisi pystyä kylläkin pelaamaan TASETULOSKAAVA tasolla silti
        if (!level) {
          siblings = branch.children.filter(i => i.TILINRO === parseInt(m, 10));

          if (!siblings.length) {
            return 0;
          } else if (this.selectedMonth) {
            level = siblings.find(g => {
              return g.TILINRO === parseInt(m, 10) && g.KIRJAUSKAUSI === this.selectedMonth;
            });
          } else {
            let thing = { ...siblings[0] };
            let sum = 0;

            siblings.forEach(g => sum = g.SUMMA);

            thing.SUMMA = sum;
            level = thing;
          }

          if (!level) {
            level = branch.children.find(g => {
              return g.TILINRO === parseInt(m, 10);
            });

            if (this.selectedMonth) {
              level = { ...level };
              level.SUMMA = 0;
            }
          }

          if (!level) {
            return 0;
          }
        }

        path += ('/' + m);
        crumbs.push({
          name: level.TILINRO ? level.TILINRO + ' ' + level.NIMI : level.NIMI,
          path
        });
        branch = level;
      }
    }

    return {
      crumbs,
      unfiltered: branch,
      siblings
    };
  } // end of  get computered() 


  @computed.struct
  get filteredBranch() {

    function recursivesum(items, akirjauskausi) {
      let sum = 0;
      items.children.forEach((item) => {
        if (item.children) {
          sum += recursivesum(item, akirjauskausi);
        } else {
          if ((akirjauskausi && item.KIRJAUSKAUSI <= akirjauskausi) || !akirjauskausi) { sum += round2d(item.SUMMA); }
        }
      })
      return round2d(sum);
    }


    if (!this.computered.unfiltered) {
      return false;
    }
    //Ei suodatusta
    let branch = { ...this.computered.unfiltered };
    //Suodatetaan filterBranch eventin mukaan
    branch = this.filterBranch(branch);

    // group matching TILINRO's jos sellainen on (alemmilla tasoilla)
    if (branch && branch.children && branch.children.length && this.TILINRO && !this.selectedMonth) {
      let grouped = {};
      const filtered = { ...branch };

      filtered.children.forEach(item => {
        if (grouped[item.TILINRO]) {
          grouped[item.TILINRO].SUMMA += round2d(item.SUMMA)
        }
        else {
          grouped[item.TILINRO] = { ...item };
        }
      }
      );

      filtered.children = Object.values(grouped);
      return filtered; //returnilla funktiosta ulos
    } else {
      if (branch && branch.children && branch.children.length && branch.children[0].TILINRO) {
        let grouped = {};
        const filtered = { ...branch };
        const akirjauskausi = this.selectedMonth;

        filtered.children.forEach(item => {
          if (grouped[item.TILINRO] || !item.TILINRO) {
            if (item.KIRJAUSKAUSI <= akirjauskausi || !akirjauskausi) {
              grouped[item.TILINRO].SUMMA += round2d(item.SUMMA)
            }
          }
          else {
            if ({ ...item }.KIRJAUSKAUSI <= akirjauskausi || !akirjauskausi) {
              grouped[item.TILINRO] = { ...item };
              if ({ ...item }.children) { grouped[item.TILINRO].SUMMA = recursivesum({ ...item }, akirjauskausi); }
              else
                if ({ ...item }.KIRJAUSKAUSI <= akirjauskausi || !akirjauskausi) {
                  if (!akirjauskausi && { ...item }.KIRJAUSKAUSI === 0) {
                    //grouped[item.TILINRO].SUMMA += { ...item }.SUMMA
                  }
                } else { grouped[item.TILINRO].SUMMA += round2d({ ...item }.SUMMA) }

            }

          };
        }
        );
        filtered.children = Object.values(grouped);
        return filtered;
      }
    }
    // tarvitaan filtteröinnissä
    if (branch && branch.children && branch.children.length && this.selectedMonth) {
      const filtered = { ...branch };
      const akirjauskausi = this.selectedMonth;

      filtered.children.forEach(item => {
        //Huomaa etta sum funktio tekee osan laskennasta
        item.SUMMA += recursivesum(item, akirjauskausi)
      });
      return filtered;
    }

    if (branch && branch.children && branch.children.length) {
      let grouped = {};
      const filtered = { ...branch };
      const akirjauskausi = this.selectedMonth;

      filtered.children.forEach(item => {
        if (grouped[item.TASETULOSKAAVA]) {
          grouped[item.TASETULOSKAAVA].SUMMA += recursivesum(item, akirjauskausi)
        }
        else {
          grouped[item.TASETULOSKAAVA] = { ...item };
          grouped[item.TASETULOSKAAVA].SUMMA += recursivesum({ ...item }, akirjauskausi);
        }
      });
      filtered.children = Object.values(grouped);
      return filtered;
    }

    return { ...branch };
  } // end of  get filteredBranch() {


  filterBranch = (branch) => {
    //jos ei dataa tai ei lapsia tai ei ole valittu kuukautta
    if (!branch || !branch.children || !this.selectedMonth) {
      //jos data ja lapsia ja ei ole valittu kuukautta
      if (branch && branch.children && !this.selectedMonth) {
        return { ...branch };
      }
      return { ...branch };
    }

    const filtered = { ...branch };

    if (filtered.children.length && !filtered.children[0].KIRJAUSKAUSI) {
      filtered.children = filtered.children.map(this.filterBranch);
    } else {
      filtered.children = filtered.children.filter(item => item.KIRJAUSKAUSI <= this.selectedMonth);
    }

    return filtered;
  } //end of filterBranch = (branch) =>

  summer(account, result) {
    if (account.SUMMA !== undefined) {
      if (!result[account.KIRJAUSKAUSI]) {
        result[account.KIRJAUSKAUSI] = {
          month: account.KIRJAUSKAUSI,
          value: 0
        };
      }
      // If is NaN we insert 0 instead!
      if (isNaN(parseFloat(account.SUMMA))) {
        result[account.KIRJAUSKAUSI].value += 0;
      } else {
        result[account.KIRJAUSKAUSI].value += parseFloat(account.SUMMA);
      }

      return;
    }

    account.children.forEach(item => this.summer(item, result));
  } // end of summer(account, result)

  @computed.struct
  get graph() {
    if (!this.computered || !this.computered.unfiltered) {
      return null;
    }
 
    var kirjauskaudet = [];
    this.paattyvatase = 0;
    this.avaavatase = 0;
    var cumulativesum = 0;
    var alkava = 0;
    const akirjauskausi = this.selectedMonth;

    if (this.computered.unfiltered && this.computered.unfiltered.children) {
      this.computered.unfiltered.children.forEach(item => this.summer(item, kirjauskaudet));
    } else {
      this.summer({ children: this.computered.siblings }, kirjauskaudet);
    }

    // taseiden laskenta 
    for (let i = 0; i <= this.profit.monthcount; i++) {

      if (kirjauskaudet[i] !== undefined) {
        if (i === 0) {
          alkava += round2d(kirjauskaudet[i].value);
          this.avaavatase = round2d(alkava);
        }
        else {
          cumulativesum = round2d(cumulativesum) + round2d(kirjauskaudet[i].value);
          kirjauskaudet[i].value = cumulativesum;
          // avaava tase edeltävän kirjauskauden mukaan jos ollaan ekalla kirjauskaudella (0 = avaavan taseen kirjauskausi)
          if (i === 0) {
            this.avaavatase = alkava;
          }
        }

        // päättyvä tase kuluvakuukausi + edeltävät summana
        if (akirjauskausi && i <= akirjauskausi) {
          this.paattyvatase = round2d(alkava + cumulativesum);
        }

        if (akirjauskausi && i < akirjauskausi) {
          this.avaavatase = round2d(alkava + cumulativesum);
        }

        kirjauskaudet[i] = {
          month: i,
          value: round2d(alkava + cumulativesum)
        };
        continue;

      } //if (array[i])

      // täytetään 'tyhjät' kumulatiivisella summalla
      kirjauskaudet[i] = {
        month: i,
        value: round2d(alkava + cumulativesum)
      };
    }

    // avaava ja päättyvä tase jos kirjauskautta ei ole valittu
    if (!akirjauskausi) {
      this.avaavatase = round2d(alkava);
    }

    if (!akirjauskausi) {
      //! kirjauskaudet[this.profit.monthcount].value on NaN! Korjaa tämä!
      this.paattyvatase = kirjauskaudet[this.profit.monthcount].value;
    }

    // pudotetaan graafista avaava tase [0] pois
    kirjauskaudet = kirjauskaudet.slice(1, kirjauskaudet.length);
    return {
      values: Object.values(kirjauskaudet),
      keys: [{ key: 'value' }]
    };
  } //end of @computed.struct

  @action.bound
  clear() {
    this.selectedMonth = null;
    this.forceUpdatingAccounts = {};

    this.accounts.clear();
    this.profits.clear();

    this.categoryLimits.clear();

    this.profitsSynced = {};
    this.accountsSynced = {};

    this.reset();
  }

  @action.bound
  reset() {
    this.scrollTop = 0;
    this.persistCategoryLimits = false;
    tilikausiStore.currentTilikausi = null;
  }

  @action.bound
  async fetchReceipts(account, month) {
    const accountToken = this.accountToken;

    if (!this.accounts.has(accountToken)) {
      this.loadingAccounts.set(accountToken, true);
    } else {
      if (this.forceUpdatingAccounts[accountToken]) {
        this.loadingAccounts.set(accountToken, true);
        this.forceUpdatingAccounts[accountToken] = false;
      } else if (this.accountsSynced[accountToken] && this.accountsSynced[accountToken] > Date.now() - CACHE_TIME) {
        return;
      }
    }

    await this.updateAccounts(account, month, accountToken);

    runInAction(() => {
      this.loadingAccounts.delete(accountToken);
    });
  }

  @action.bound
  async updateAccounts(account, month, accountToken) {
    try {
      let url = baseurl + account.TILINRO;

      if (month) {
        url += ('/' + month);
      }

      url += ('?tilikausiid=' + this.currentTilikausiId);

      const receipts = await Http.crequest(url);

      this.accountsSynced[accountToken] = new Date();

      // check if updated
      const cached = this.accounts.get(accountToken);

      // categorycount
      let update = this.shouldAccountsUpdate(cached, receipts);

      if (update) {
        // add ui props
        receipts.forEach(category => {
          category.hasAll = category.items.length < SHOW_MORE_LIMIT;
        });

        runInAction(() => {
          this.accounts.set(accountToken, receipts);

          this.persistCache();
        });
      }
    } catch (error) {
      viewStore.showSnackbar(parseError());
    }
  }

  shouldAccountsUpdate(cached, receipts) {
    let updated = !cached || cached.length !== receipts.length;

    if (!updated) {
      for (let i in receipts) {
        updated = Math.min(cached[i].items.length, SHOW_MORE_LIMIT) !== Math.min(receipts[i].items.length, SHOW_MORE_LIMIT);

        if (updated) return updated;

        for (let j in receipts[i].items) {
          updated = shallowDiffers(cached[i].items[j], receipts[i].items[j]);

          if (updated) return updated;
        }
      }
    }

    return updated;
  }

  @action.bound
  resetCategoryLimits() {
    if (!this.persistCategoryLimits) {
      this.categoryLimits.clear();
    }

    this.persistCategoryLimits = false;
  }

  @action.bound
  showMore(account, month, category, index) {
    this.categoryLimits.set(index, (this.categoryLimits.get(index) || SHOW_MORE_LIMIT) + 25);
  }

  @action.bound
  async fetchMore(account, month, category, index) {
    this.categoryLimits.set(index, (this.categoryLimits.get(index) || SHOW_MORE_LIMIT) + 25);

    const categoryToken = [this.profitToken, account.TILINRO, category.id, month].join('.');

    const accountToken = this.accountToken;

    this.loadingCategories.set(categoryToken, true);

    try {
      let url = baseurl + account.TILINRO;

      if (month) {
        url += ('/' + month);
      }

      url += '?tositelajiid=' + category.id + '&offset=' + category.items.length + '&tilikausiid=' + this.currentTilikausiId;

      const receipts = await Http.crequest(url);

      const acc = this.accounts.get(accountToken);
      const cat = acc[index];

      runInAction(() => {
        cat.items = cat.items.concat(receipts);

        if (receipts.length < 25) {
          cat.hasAll = true;
        }
      });

      this.persistCache();
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }

    runInAction(() => {
      this.loadingCategories.delete(categoryToken);
    });
  } // end of async fetchMore(account, month, category, index) {

  persistCache() {
    Storage.set('accounts', this.accounts);
  }

  @action.bound
  drill(account, index) {
    routing.push(routing.location.pathname + '/' + (account.TILINRO || index));
  }

  @action.bound
  async fetch() {
    if (!this.hasModule) {
      routing.push('/tasevastattavaa');
      return;
    }

    const profitToken = this.profitToken;

    if (this.profits.has(profitToken)) {
      if (this.profitsSynced[profitToken] && this.profitsSynced[profitToken] > Date.now() - CACHE_TIME) {
        if(this.profits['empty'] === 'true'){
          runInAction(() => {
            this.noData = true;
          });
        }
        else{
          runInAction(() => {
            this.noData = false;
          });
        }
        return;
      }
      this.loadingProfitsBackground.set(profitToken, true);
    } else {
      this.loadingProfits.set(profitToken, true);
    }

    await this.update();

    runInAction(() => {
      this.loadingProfits.delete(profitToken);
      this.loadingProfitsBackground.delete(profitToken);
    });
  }

  @action.bound
  async refresh() {
    const profitToken = this.profitToken;

    if (this.loadingProfits.has(profitToken)) {
      return;
    }

    if (this.filteredBranch.TILINRO) {
      const accountToken = this.accountToken;

      this.forceUpdatingAccounts[accountToken] = true;
    }

    this.loadingProfits.set(profitToken, true);

    await this.update();

    runInAction(() => {
      this.loadingProfits.delete(profitToken);
    });
  }

  @action.bound
  async update() {
    try {
      const profitToken = this.profitToken;

      let url = boundurl;

      url += ('?tilikausiid=' + this.currentTilikausiId);

      const profits = await Http.crequest(url);

      // If empty is true we set [noData] to true
      if (profits['empty'] === 'true') {
        runInAction(() => {
          this.noData = true;
        });
      }
      else {
        runInAction(() => {
          this.noData = false;
          this.profits.set(profitToken, profits);
        });

        Storage.set('profits', this.profits);

        this.profitsSynced[profitToken] = Date.now();

      }
    } catch (error) {
      viewStore.showSnackbar(parseError(error));
    }
  }

  @action.bound
  onMonthSelect(month) {
    this.selectedMonth = month;
  }

  sum(account) {

    function recursivesum(items, akirjauskausi) {
      let sum = 0;
      items.children.forEach((item) => {
        if (item.children) {
          sum += round2d(recursivesum(item, akirjauskausi));
        } else {

          if ((!akirjauskausi || item.KIRJAUSKAUSI <= akirjauskausi)) { sum += round2d(item.SUMMA); }
        }
      })
      return sum;
    }

    const akirjauskausi = this.selectedMonth;

    if ((account.SUMMA !== undefined) || (account.branch)) {
      //Osalla onkin tiedot accountin branchilla!
      if (account.branch && (account.branch.SUMMA !== undefined)) {
        if (account.branch.KIRJAUSKAUSI <= akirjauskausi) { return account.branch.SUMMA }
        else return 0;
      }
      else {
        if (account.children) { return recursivesum(account, akirjauskausi) }
        else return account.SUMMA;
      }
    }

    if (!account.children) {
      return;
    }

    let sum = account.children.reduce((total, level) => {
      if (level.KIRJAUSKAUSI === 0 && !akirjauskausi) { return total; }
      else { return total + this.sum(level); }
    }, 0);
    return sum;
  }

  saveScroll() {
    this.scrollTop = this.pageContentRef.scrollTop;
    this.persistCategoryLimits = true;
  }

  resetScroll() {
    this.scrollTop = 0;
    this.persistCategoryLimits = false;
  }
}

export default new tasevastaavaaStore();
